import React, { useCallback, useEffect, useRef, useState } from 'react'
import { View, Platform, TouchableOpacity, StyleSheet, Modal } from 'react-native'
import * as Clipboard from 'expo-clipboard'
import QRCode from 'react-native-qrcode-svg'

import { DS } from '@/design-system'
import { useStore } from '@/models'
import { useToastStore } from '@/navigation/toast'
import { useGameOptionsDrawerStore } from './GameOptionsDrawer'
import { useAVSheetStore } from '../../components/AV/useAVSheetStore'
import { getShareUrl, promptShareUrl } from '../../utils/shareUrl'
import { SocialIcons } from '../../components/ShareCodeSheet'
import { BaseBottomSheet, Center } from './BaseBottomSheet'

interface WaitingForPlayersSheetProps {
  code: string
}

export const WaitingForPlayersSheet = ({ code }: WaitingForPlayersSheetProps) => {
  const { share } = useStore()
  const hasTriggeredShareRef = useRef(false)

  // State for QR code + social share modal (web)
  const [showShareQRModal, setShowShareQRModal] = useState(false)
  // State for QR code display
  const [showQRCode, setShowQRCode] = useState(false)
  const [qrUrl, setQrUrl] = useState<string | null>(null)

  // Initialize QR URL on web immediately
  useEffect(() => {
    if (Platform.OS === 'web') {
      const initializeQRUrl = async () => {
        const url = await promptShareUrl(code)
        setQrUrl(url)
      }
      initializeQRUrl()
    }
  }, [code])

  const isDrawerOpen = useGameOptionsDrawerStore((s) => s.status === 'open')
  const isAVOpen = useAVSheetStore((s) => s.isOpen)

  const shareMessage = `${code}: Join my game on Poker Power`

  const handleCopyCode = useCallback(async () => {
    await Clipboard.setStringAsync(code)
    useToastStore.getState().enqueueToast({
      kind: 'success',
      message: `Code copied: ${code}`
    })
  }, [code])

  const handleShare = useCallback(async () => {
    if (Platform.OS !== 'web') {
      // Get share URL (use production URL for native share - works as universal link)
      const shareUrl = getShareUrl(code)
      // On mobile, use native share - opens native share sheet
      await share.promptShare(shareMessage, shareMessage, shareUrl)
    }
  }, [share, shareMessage, code])

  const handleCopyLink = useCallback(async () => {
    // In __DEV__, prompt user to choose web or mobile URL
    const url = await promptShareUrl(code)
    await Clipboard.setStringAsync(url)
    useToastStore.getState().enqueueToast({
      kind: 'success',
      message: 'Link copied to clipboard!'
    })
  }, [code])

  const handleShowQR = useCallback(async () => {
    // In __DEV__, prompt user to choose web or mobile URL
    const shareUrl = await promptShareUrl(code)
    setQrUrl(shareUrl)
    setShowQRCode(true)
  }, [code])

  // Auto-open share when sheet is presented
  const handleSheetChange = useCallback(
    (index: number) => {
      // When sheet opens (index changes from -1 to 0 or higher), trigger share once
      // Note: was also checking bottom modal's `hasPresentedRef` before refactoring. Works without.
      // If needed, add as prop and pass ref to the BaseBottomModal.
      if (index >= 0 && !hasTriggeredShareRef.current) {
        hasTriggeredShareRef.current = true
        // Small delay to ensure sheet is fully visible before showing share
        setTimeout(() => {
          handleShare()
        }, 300)
      }
    },
    [handleShare]
  )

  return (
    <>
      <BaseBottomSheet
        dismissOnOverlays={[isDrawerOpen, isAVOpen]}
        onChange={handleSheetChange}
        contentContainerStyle={styles.container}
      >
        {!showQRCode ? (
          <>
            {/* Game Code Display */}
            <TouchableOpacity onPress={handleCopyCode} activeOpacity={0.7}>
              <View style={styles.codeContainer}>
                <DS.Frame row alignItems="center" justifyContent="space-around" style={{ width: '100%' }}>
                  <DS.Frame row alignItems="center" gap="12" flex={1}>
                    <DS.Text variant="Heading 1/Medium" style={styles.codeText}>
                      {code}
                    </DS.Text>
                    <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
                      Copy
                    </DS.Text>
                  </DS.Frame>
                  <DS.Frame style={{ marginLeft: 12 }}>
                    <DS.Icon name="Copy" size={20} color="Brand/Dark Grey" />
                  </DS.Frame>
                </DS.Frame>
              </View>
            </TouchableOpacity>

            {/* Waiting Message */}
            <View style={styles.messageContainer}>
              <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey" textAlign="center">
                Waiting for other players to join...
              </DS.Text>
            </View>

            {/* Action Buttons */}
            <Center style={{ width: '100%', maxWidth: 250, gap: 12 }}>
              {Platform.OS === 'web' ? (
                <>
                  <DS.Button
                    variant="Large/Primary"
                    type="On Light"
                    onPress={handleCopyLink}
                    style={{ width: '100%' }}
                    maxWidth
                  >
                    Copy Link
                  </DS.Button>
                  {qrUrl && (
                    <DS.Button
                      variant="Large/Secondary"
                      type="On Dark"
                      onPress={() => setShowShareQRModal(true)}
                      style={{ width: '100%' }}
                      maxWidth
                    >
                      Share & QR Code
                    </DS.Button>
                  )}
                </>
              ) : (
                <>
                  <DS.Button variant="Large/Primary" type="On Light" onPress={handleShare} maxWidth>
                    Share Invite
                  </DS.Button>
                  <DS.Button variant="Large/Secondary" type="On Dark" onPress={handleShowQR} maxWidth>
                    Show QR Code
                  </DS.Button>
                </>
              )}
            </Center>
          </>
        ) : (
          <>
            {/* QR Code Display (Mobile only - when toggled) */}
            <View style={styles.qrSection}>
              <DS.Text variant="Heading 3/Medium" color="Brand/Dark Grey" textAlign="center" style={styles.qrTitle}>
                Scan to Join
              </DS.Text>
              <DS.Text
                variant="Body Small/Regular"
                color="Brand/Dark Grey"
                textAlign="center"
                style={styles.qrSubtitle}
              >
                Allow people near you to scan the code with their camera app to instantly join your game.
              </DS.Text>
              {qrUrl && (
                <View style={styles.qrContainer}>
                  <QRCode value={qrUrl} size={200} />
                </View>
              )}
              <DS.Button variant="Large/Secondary" type="On Dark" onPress={() => setShowQRCode(false)} maxWidth>
                Back
              </DS.Button>
            </View>
          </>
        )}
      </BaseBottomSheet>

      {/* Share & QR Code Modal (Web) */}
      {Platform.OS === 'web' && qrUrl && (
        <Modal
          visible={showShareQRModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowShareQRModal(false)}
        >
          <TouchableOpacity
            style={styles.shareModalBackdrop}
            activeOpacity={1}
            onPress={() => setShowShareQRModal(false)}
          >
            <TouchableOpacity style={styles.shareModalContent} activeOpacity={1} onPress={(e) => e.stopPropagation()}>
              <DS.Frame style={styles.shareModalHeader}>
                <DS.Text variant="Heading 3/Medium" color="Brand/Dark Grey">
                  Share & QR Code
                </DS.Text>
                <TouchableOpacity
                  onPress={() => setShowShareQRModal(false)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <DS.Icon name="Close" size={24} color="Brand/Dark Grey" />
                </TouchableOpacity>
              </DS.Frame>

              {/* QR Code */}
              <View style={styles.qrSection}>
                <DS.Text variant="Heading 5/Medium" color="Brand/Dark Grey" textAlign="center">
                  Scan to Join
                </DS.Text>
                <DS.Text
                  variant="Body Small/Regular"
                  color="Brand/Dark Grey"
                  textAlign="center"
                  style={styles.qrSubtitle}
                >
                  Allow people near you to scan the code with their camera app to instantly join your game.
                </DS.Text>
                <View style={styles.qrContainer}>
                  <QRCode value={qrUrl} size={200} />
                </View>
              </View>

              {/* Social Share Icons */}
              <DS.Frame style={styles.shareModalSocialSection}>
                <DS.Text variant="Body Small/Medium" color="Brand/Dark Grey" textAlign="center">
                  Or share via
                </DS.Text>
                <SocialIcons title={shareMessage} shareUrl={qrUrl} />
              </DS.Frame>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    minHeight: 200,
    paddingVertical: 16
  },
  codeContainer: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2
  },
  codeText: {
    letterSpacing: 6,
    color: DS.palette['Brand/Maroon']
  },
  messageContainer: {
    paddingVertical: 8
  },
  qrSection: {
    alignItems: 'center',
    gap: 16,
    width: '100%'
  },
  qrTitle: {
    marginBottom: 8
  },
  qrSubtitle: {
    paddingHorizontal: 16,
    marginBottom: 8
  },
  qrContainer: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center'
  },
  modalContent: {
    width: '90%',
    maxWidth: 500,
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    overflow: 'hidden'
  },
  shareModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center'
  },
  shareModalContent: {
    width: '90%',
    maxWidth: 480,
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    padding: 24,
    gap: 24
  },
  shareModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  shareModalSocialSection: {
    gap: 12,
    paddingTop: 8
  }
})
